import { isTableId, type Member } from "@app/protocol";
import { DiscordSDK } from "@discord/embedded-app-sdk";
import { API_BASE, CLIENT_ID, MOCK_USER, VOICE } from "../config";
import { withTimeout } from "./withTimeout";

export interface Handshake {
  member: Member;
  /** Server-signed session for the table socket. */
  session: string;
  instanceId: string;
  /** A table id carried by an Activity link (`shareLink` custom_id); null when opened plainly. */
  customId: string | null;
  /** The server the activity runs in; null in DMs and in mock mode. Untrusted, used for store custom data. */
  guildId: string | null;
  sdk: DiscordSDK | null;
}

export type HandshakeError = "denied" | "server" | "refresh";

/** Where the handshake is; shown on screen so a stall points at a step. */
export type HandshakeStage = "ready" | "authorize" | "token" | "authenticate";
export const HANDSHAKE_STAGES: readonly HandshakeStage[] = [
  "ready",
  "authorize",
  "token",
  "authenticate",
];

/** `sdk.ready()` never rejects; without the Discord client it waits forever. */
const READY_TIMEOUT_MS = 15_000;
/** The consent sheet waits for a person the first time; the other steps only for machines (0027 C6). */
const AUTHORIZE_TIMEOUT_MS = 120_000;
const STEP_TIMEOUT_MS = 15_000;

/** What the failure screen shows when `ready` never comes: enough to tell which side is silent. */
export interface HandshakeDiagnostics {
  frameId: string | null;
  platform: string | null;
  referrer: string;
  nested: boolean;
  received: string[];
  /** The SDK's own message when a command rejects. */
  reason?: string;
}

export function collectDiagnostics(): HandshakeDiagnostics {
  const params = new URLSearchParams(window.location.search);
  const diag: HandshakeDiagnostics = {
    frameId: params.get("frame_id"),
    platform: params.get("platform"),
    referrer: document.referrer,
    nested: window.parent !== window,
    received: [],
  };
  window.addEventListener("message", (event) => {
    if (diag.received.length >= 8) return;
    if (!Array.isArray(event.data)) return diag.received.push(typeof event.data);
    const [opcode, payload] = event.data as [unknown, unknown];
    // Opcode 2 is CLOSE: the client refused the handshake and says why.
    const detail =
      opcode === 2 && payload !== null && typeof payload === "object"
        ? ` ${JSON.stringify(payload)}`
        : "";
    diag.received.push(`${String(opcode)}@${event.origin}${detail}`);
  });
  return diag;
}

/** A handshake step with a limit; running out is a failure of its own kind. */
function limited<T>(
  promise: Promise<T>,
  ms: number,
  code: HandshakeError,
  diag: HandshakeDiagnostics,
): Promise<T> {
  return withTimeout(promise, ms, () => new HandshakeFailed(code, diag));
}

export class HandshakeFailed extends Error {
  readonly code: HandshakeError;
  readonly diagnostics: HandshakeDiagnostics | null;
  constructor(code: HandshakeError, diagnostics: HandshakeDiagnostics | null = null) {
    super(code);
    this.code = code;
    this.diagnostics = diagnostics;
  }
}

/** The local stand-in token; `?guild=` rides along so the server can answer guild questions. */
function mockSession(userId: string): string {
  const guild = new URLSearchParams(window.location.search).get("guild");
  return guild === null || guild === "" ? `mock:${userId}` : `mock:${userId}:${guild}`;
}

/** Local development without Discord: a mock member and a `mock:` session (server: local only). */
function mockHandshake(userId: string): Handshake {
  return {
    member: { userId, name: userId, avatar: null },
    session: mockSession(userId),
    instanceId: new URLSearchParams(window.location.search).get("instance") ?? "local-table",
    customId: new URLSearchParams(window.location.search).get("table"),
    guildId: new URLSearchParams(window.location.search).get("guild"),
    sdk: null,
  };
}

/**
 * Discord answers the handshake once per frame (embedded-app-sdk issue #41), so
 * the SDK is created once and a retry reuses it.
 */
let shared: { sdk: DiscordSDK; diag: HandshakeDiagnostics; ready: boolean } | null = null;

async function exchange(
  code: string,
  /** The Activity instance, so the server can verify which Discord server we are in (slice 0023). */
  instance: string,
): Promise<{ access_token: string; session: string; member: Member }> {
  const response = await fetch(`${API_BASE}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, instance }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: unknown } | null;
    const reason =
      `token ${response.status} ${typeof body?.detail === "string" ? body.detail : ""}`.trim();
    throw new HandshakeFailed(response.status === 401 ? "denied" : "server", {
      ...(shared?.diag ?? collectDiagnostics()),
      reason,
    });
  }
  return (await response.json()) as { access_token: string; session: string; member: Member };
}

async function readySdk(onStage: (stage: HandshakeStage) => void): Promise<DiscordSDK> {
  if (shared === null) {
    const diag = collectDiagnostics();
    shared = { sdk: new DiscordSDK(CLIENT_ID), diag, ready: false };
  }
  if (!shared.ready) {
    onStage("ready");
    await limited(shared.sdk.ready(), READY_TIMEOUT_MS, "refresh", shared.diag);
    shared.ready = true;
  }
  return shared.sdk;
}

type Scope = "identify" | "rpc.activities.write" | "rpc.voice.read";

async function authorizeCode(sdk: DiscordSDK, scope: Scope[]): Promise<string> {
  const { code } = await sdk.commands.authorize({
    client_id: CLIENT_ID,
    response_type: "code",
    state: "",
    prompt: "none",
    scope,
  });
  return code;
}

/**
 * rpc.activities.write: Rich Presence (setActivity) on the player's profile. rpc.voice.read: who
 * is speaking (decision 146), only where the flag is on; until Discord approves the scope it may be
 * refused, and the player signs in without it rather than not at all.
 */
async function authorizeScopes(sdk: DiscordSDK): Promise<string> {
  const base: Scope[] = ["identify", "rpc.activities.write"];
  if (!VOICE) return authorizeCode(sdk, base);
  try {
    return await authorizeCode(sdk, [...base, "rpc.voice.read"]);
  } catch {
    return authorizeCode(sdk, base);
  }
}

/** `authorize` → server exchange → `authenticate` (review 0001 S1). */
export async function handshake(onStage: (stage: HandshakeStage) => void): Promise<Handshake> {
  if (MOCK_USER !== null) return mockHandshake(MOCK_USER);
  const sdk = await readySdk(onStage);
  onStage("authorize");
  let code: string;
  const diag = () => shared?.diag ?? collectDiagnostics();
  try {
    code = await limited(authorizeScopes(sdk), AUTHORIZE_TIMEOUT_MS, "denied", diag());
  } catch (error) {
    if (error instanceof HandshakeFailed) throw error;
    const reason = error instanceof Error ? error.message : String(error);
    throw new HandshakeFailed("denied", { ...(shared?.diag ?? collectDiagnostics()), reason });
  }
  onStage("token");
  const { access_token, session, member } = await limited(
    exchange(code, sdk.instanceId),
    STEP_TIMEOUT_MS,
    "server",
    { ...diag(), reason: "token timed out" },
  );
  onStage("authenticate");
  await limited(sdk.commands.authenticate({ access_token }), STEP_TIMEOUT_MS, "server", {
    ...diag(),
    reason: "authenticate timed out",
  });
  return {
    member,
    session,
    instanceId: sdk.instanceId,
    // Discord may send `custom_id=undefined`: that is no table link (13 Sept 2026).
    customId: isTableId(sdk.customId) ? sdk.customId : null,
    guildId: sdk.guildId,
    sdk,
  };
}

/**
 * A table link: Discord's share sheet inside the Activity; outside Discord the link
 * goes to the clipboard. True when it went to the clipboard, so the caller can say so.
 */
export async function shareTable(
  sdk: DiscordSDK | null,
  tableId: string,
  message: string,
): Promise<boolean> {
  if (sdk === null) {
    const url = new URL(window.location.href);
    url.searchParams.set("table", tableId);
    await navigator.clipboard?.writeText(url.toString());
    return true;
  }
  await sdk.commands.shareLink({ message, custom_id: tableId });
  return false;
}

/** External links (checkout) must go through the SDK so Discord shows its consent modal. */
export async function openExternal(sdk: DiscordSDK | null, url: string): Promise<void> {
  if (sdk === null) window.open(url, "_blank", "noopener");
  else await sdk.commands.openExternalLink({ url });
}
